import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Bell, Mail, Clock, Send, CheckCircle, Users, User, ChevronDown, ChevronUp, Link2, AlertTriangle, Settings, X, BarChart2 } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import './NotificationsView.css';
import MetricAlertsCard from './MetricAlertsCard';
import AccountAccessAlertsCard from './AccountAccessAlertsCard';
import googleAdsLogo from '../logos/google-ads.png';
import microsoftAdsLogo from '../logos/microsoft-ads.png';
import metaAdsLogo from '../logos/meta-ads.png';
import pinterestAdsLogo from '../logos/pinterest-ads.png';
import tiktokAdsLogo from '../logos/tiktok-ads.png';
import chatgptAdsLogo from '../logos/chatgpt-ads.png';
import awinLogo from '../logos/awin.png';

const API_URL = config.API_URL;

/** Logo de chaque régie proposée au digest. Voir `TYPES_DIGEST` plus bas. */
const LOGOS_DIGEST = {
  GOOGLE_ADS: { src: googleAdsLogo, alt: 'Google Ads' },
  MICROSOFT_ADS: { src: microsoftAdsLogo, alt: 'Microsoft Ads' },
  FACEBOOK_ADS: { src: metaAdsLogo, alt: 'Meta Ads' },
  PINTEREST_ADS: { src: pinterestAdsLogo, alt: 'Pinterest Ads' },
  TIKTOK_ADS: { src: tiktokAdsLogo, alt: 'TikTok Ads' },
  OPENAI_ADS: { src: chatgptAdsLogo, alt: 'ChatGPT Ads' },
  AWIN: { src: awinLogo, alt: 'Awin' }
};

const NotificationsView = ({ token, user, userOrganizations }) => {
  // Vérifier si l'utilisateur a accès aux fonctionnalités de diagnostic
  // = ADMIN Kokpit OU membre d'une organisation (owner ou membre)
  const isOrgMember = userOrganizations?.length > 0;
  const hasDiagnosticAccess = user?.role === 'ADMIN' || isOrgMember;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [prefs, setPrefs] = useState({
    emailAlertsActive: true,
    consolidatedEmailEnabled: true,
    consolidatedEmailAccounts: [],
    individualEmailAccounts: [],
    connectorExpirationAlerts: true
  });
  const [showConsolidatedAccounts, setShowConsolidatedAccounts] = useState(false);
  const [showIndividualAccounts, setShowIndividualAccounts] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  // État pour le Digest
  const [digestPrefs, setDigestPrefs] = useState({
    digestEmailActive: false,
    digestEmailAccounts: []
  });
  const [digestAccounts, setDigestAccounts] = useState([]);
  const [savingDigest, setSavingDigest] = useState(false);
  const [showDigestConfigModal, setShowDigestConfigModal] = useState(false);
  const [showDigestAccounts, setShowDigestAccounts] = useState(false);
  const [testDigestLoading, setTestDigestLoading] = useState(false);
  const [testDigestResult, setTestDigestResult] = useState(null);

  // Charger les préférences et les comptes
  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      // Charger les préférences diagnostic
      const prefsResponse = await axios.get(`${API_URL}/api/diagnostic/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (prefsResponse.data.success) {
        setPrefs(prefsResponse.data.data);
      }

      // Charger tous les comptes depuis la bibliothèque
      const libraryResponse = await axios.get(`${API_URL}/api/library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (libraryResponse.data.success && libraryResponse.data.library) {
        // Comptes Google Ads pour diagnostic
        const googleAdsAccounts = libraryResponse.data.library.GOOGLE_ADS || [];
        const formattedAccounts = googleAdsAccounts.map(account => ({
          accountId: account.sourceId,
          accountName: account.sourceName || account.sourceId
        }));
        // Trier les comptes par ordre alphabétique
        const sortedAccounts = [...formattedAccounts].sort((a, b) =>
          (a.accountName || '').localeCompare(b.accountName || '', 'fr', { sensitivity: 'base' })
        );
        setAccounts(sortedAccounts);

        /**
         * Les comptes proposés au digest — **toutes** les régies qu'il sait lire.
         *
         * La liste s'arrêtait à Google, Microsoft et Meta. Awin était pourtant collecté côté
         * serveur : un utilisateur qui personnalisait sa sélection le perdait donc de son
         * digest sans que rien ne le dise, puisqu'il ne pouvait pas le cocher.
         *
         * Cette liste doit suivre `accountTypes` dans `DigestEmailService` : un type ici
         * sans branche là-bas se coche sans jamais apparaître dans l'e-mail.
         */
        const TYPES_DIGEST = [
          'GOOGLE_ADS', 'MICROSOFT_ADS', 'FACEBOOK_ADS',
          'PINTEREST_ADS', 'TIKTOK_ADS', 'OPENAI_ADS', 'AWIN'
        ];
        const allAdsAccounts = TYPES_DIGEST.flatMap(type => (
          (libraryResponse.data.library[type] || []).map(a => ({
            accountId: a.sourceId,
            accountName: a.sourceName || a.sourceId,
            type
          }))
        ));
        setDigestAccounts(allAdsAccounts);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }

    // Charger les préférences digest (séparé pour ne pas bloquer si l'API n'existe pas encore)
    try {
      const digestPrefsResponse = await axios.get(`${API_URL}/api/digest/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (digestPrefsResponse.data.success) {
        setDigestPrefs(digestPrefsResponse.data.data);
      }
    } catch (error) {
      console.error('Erreur chargement préférences digest:', error);
    }

    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sauvegarder les préférences
  const savePrefs = async (newPrefs) => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/diagnostic/preferences`,
        newPrefs,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPrefs(response.data.data);
      }
    } catch (error) {
      console.error('Erreur sauvegarde préférences:', error);
    } finally {
      setSaving(false);
    }
  };

  // Helpers pour gérer les sélections
  const normalizeId = (id) => id?.replace(/-/g, '') || '';

  const isAccountInList = (accountId, list) => {
    const normalizedList = (list || []).map(normalizeId);
    return normalizedList.includes(normalizeId(accountId));
  };

  const toggleAccountInList = (accountId, list, setList) => {
    const normalized = normalizeId(accountId);
    const normalizedList = (list || []).map(normalizeId);

    let newList;
    if (normalizedList.includes(normalized)) {
      newList = list.filter(id => normalizeId(id) !== normalized);
    } else {
      newList = [...(list || []), accountId];
    }
    return newList;
  };

  // Handlers
  const handleMasterToggle = (checked) => {
    savePrefs({ ...prefs, emailAlertsActive: checked });
  };

  const handleConsolidatedToggle = (checked) => {
    savePrefs({ ...prefs, consolidatedEmailEnabled: checked });
  };

  const handleConnectorExpirationToggle = (checked) => {
    savePrefs({ ...prefs, connectorExpirationAlerts: checked });
  };

  const handleConsolidatedAccountToggle = (accountId) => {
    const newList = toggleAccountInList(accountId, prefs.consolidatedEmailAccounts);
    savePrefs({ ...prefs, consolidatedEmailAccounts: newList });
  };

  const handleIndividualAccountToggle = (accountId) => {
    const newList = toggleAccountInList(accountId, prefs.individualEmailAccounts);
    savePrefs({ ...prefs, individualEmailAccounts: newList });
  };

  const handleSelectAllConsolidated = () => {
    savePrefs({ ...prefs, consolidatedEmailAccounts: [] }); // vide = tous
  };

  const handleSelectAllIndividual = () => {
    const allIds = accounts.map(a => a.accountId);
    savePrefs({ ...prefs, individualEmailAccounts: allIds });
  };

  const handleSelectNoneIndividual = () => {
    savePrefs({ ...prefs, individualEmailAccounts: [] });
  };

  // Handlers Digest
  const saveDigestPrefs = async (newPrefs) => {
    setSavingDigest(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/digest/preferences`,
        newPrefs,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setDigestPrefs(response.data.data);
      }
    } catch (error) {
      console.error('Erreur sauvegarde préférences digest:', error);
    } finally {
      setSavingDigest(false);
    }
  };

  const handleDigestToggle = (checked) => {
    saveDigestPrefs({ ...digestPrefs, digestEmailActive: checked });
  };

  const handleDigestAccountToggle = (accountId) => {
    const newList = toggleAccountInList(accountId, digestPrefs.digestEmailAccounts);
    saveDigestPrefs({ ...digestPrefs, digestEmailAccounts: newList });
  };

  const handleSelectAllDigest = () => {
    saveDigestPrefs({ ...digestPrefs, digestEmailAccounts: [] }); // vide = tous
  };

  const sendTestDigestEmail = async () => {
    setTestDigestLoading(true);
    setTestDigestResult(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/digest/test-email`,
        { accountIds: digestPrefs.digestEmailAccounts?.length > 0 ? digestPrefs.digestEmailAccounts : null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setTestDigestResult({ success: true, message: response.data.message });
      } else {
        setTestDigestResult({ success: false, message: response.data.error || 'Erreur' });
      }
    } catch (error) {
      setTestDigestResult({ success: false, message: error.response?.data?.error || error.message });
    } finally {
      setTestDigestLoading(false);
    }
  };

  const digestAccountsCount = digestPrefs.digestEmailAccounts?.length || 0;

  // Test email
  const sendTestEmail = async (type) => {
    setTestEmailLoading(true);
    setTestEmailResult(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/diagnostic/test-email`,
        { type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setTestEmailResult({ success: true, message: response.data.message });
      } else {
        setTestEmailResult({ success: false, message: response.data.error || 'Erreur' });
      }
    } catch (error) {
      setTestEmailResult({ success: false, message: error.response?.data?.error || error.message });
    } finally {
      setTestEmailLoading(false);
    }
  };

  const consolidatedAccountsCount = prefs.consolidatedEmailAccounts?.length || 0;
  const individualAccountsCount = prefs.individualEmailAccounts?.length || 0;

  if (loading) {
    return (
      <div className="step-content notifications-view">
        <div className="notifications-loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="step-content notifications-view">
      {/* Header style Connecteurs */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>
          <Bell size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Notifications
        </h2>
      </div>

      <div className="notifications-content">
        {/* Grille 2 colonnes pour les alertes principales */}
        <div className="notifications-grid">
          {/* Alertes expiration token connecteurs */}
          <div className="notification-card">
            <div className="notification-card-header">
              <Link2 size={24} />
              <h2>Alertes expiration des connecteurs</h2>
            </div>

            <div className="notification-card-body">
              <div className="notification-option">
                <div className="notification-option-info">
                  <h3>Notification token expiré</h3>
                  <p>
                    Recevez une alerte par email lorsqu'un de vos connecteurs (Google Ads, Google Analytics, Meta Ads, etc.) nécessite une reconnexion.
                  </p>
                  <div className="notification-schedule" style={{ color: '#f59e0b' }}>
                    <AlertTriangle size={16} />
                    <span>Envoi immédiat en cas d'expiration détectée</span>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={prefs.connectorExpirationAlerts}
                    onChange={(e) => handleConnectorExpirationToggle(e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          {/* Master Switch - seulement pour abonnés/ADMIN */}
          {hasDiagnosticAccess && (
            <div className="notification-card">
              <div className="notification-card-header">
                <Mail size={24} />
                <h2>Alertes diagnostic par email</h2>
                {prefs.emailAlertsActive && accounts.length > 0 && (
                  <button
                    className="config-btn"
                    onClick={() => setShowConfigModal(true)}
                    style={{
                      marginLeft: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      background: '#1a1a1a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    <Settings size={16} />
                    Paramétrer
                  </button>
                )}
              </div>

              <div className="notification-card-body">
                <div className="notification-option">
                  <div className="notification-option-info">
                    <h3>Activer les notifications</h3>
                    <p>
                      Recevez chaque matin un rapport de diagnostic de vos comptes Google Ads.
                    </p>
                    <div className="notification-schedule">
                      <Clock size={16} />
                      <span>Envoi quotidien à 6h00</span>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={prefs.emailAlertsActive}
                      onChange={(e) => handleMasterToggle(e.target.checked)}
                      disabled={saving}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Comptes devenus inaccessibles, tous connecteurs confondus.

              Placée ici, sous les alertes d'expiration de connecteur : les deux disent qu'un
              accès ne fonctionne plus, l'une parce que le jeton a expiré, l'autre parce que
              le client l'a coupé. Les séparer par le digest aurait éloigné deux cartes qu'on
              vient consulter pour la même raison. */}
          <AccountAccessAlertsCard token={token} />

          {/* Digest quotidien */}
          <div className="notification-card">
            <div className="notification-card-header">
              <BarChart2 size={24} />
              <h2>Digest quotidien</h2>
              {digestPrefs.digestEmailActive && digestAccounts.length > 0 && (
                <button
                  className="config-btn"
                  onClick={() => setShowDigestConfigModal(true)}
                  style={{
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    background: '#1a1a1a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  <Settings size={16} />
                  Paramétrer
                </button>
              )}
            </div>

            <div className="notification-card-body">
              <div className="notification-option">
                <div className="notification-option-info">
                  <h3>Activer le digest</h3>
                  <p>
                    Recevez chaque matin un récapitulatif des dépenses de vos comptes
                    publicitaires : Google Ads, Microsoft Ads, Meta Ads, Pinterest Ads,
                    TikTok Ads, ChatGPT Ads et Awin.
                  </p>
                  <div className="notification-schedule">
                    <Clock size={16} />
                    <span>Envoi quotidien à 6h00</span>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={digestPrefs.digestEmailActive}
                    onChange={(e) => handleDigestToggle(e.target.checked)}
                    disabled={savingDigest}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          {/* Alertes de dépassement sur les 24 dernières heures (Google Ads) */}
          <MetricAlertsCard token={token} />
        </div>

        {/* Zone de test - dev uniquement */}
        {process.env.NODE_ENV === 'development' && (
          <div className="notification-dev-test">
            <div className="dev-test-header">
              <span className="dev-badge">DEV</span>
              <span>Zone de test</span>
            </div>
            <div className="test-buttons">
              <button
                onClick={() => sendTestEmail('consolidated')}
                disabled={testEmailLoading}
                className="test-email-button"
              >
                <Send size={18} />
                {testEmailLoading ? 'Envoi...' : 'Tester email consolidé'}
              </button>
              <button
                onClick={() => sendTestEmail('individual')}
                disabled={testEmailLoading}
                className="test-email-button secondary"
              >
                <Send size={18} />
                {testEmailLoading ? 'Envoi...' : 'Tester email individuel'}
              </button>
              <button
                onClick={() => sendTestEmail('connector-expiration')}
                disabled={testEmailLoading}
                className="test-email-button"
                style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
              >
                <AlertTriangle size={18} />
                {testEmailLoading ? 'Envoi...' : 'Tester alerte expiration'}
              </button>
              <button
                onClick={sendTestDigestEmail}
                disabled={testDigestLoading}
                className="test-email-button"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
              >
                <BarChart2 size={18} />
                {testDigestLoading ? 'Envoi...' : 'Tester email digest'}
              </button>
            </div>
            {testEmailResult && (
              <div className={`test-email-result ${testEmailResult.success ? 'success' : 'error'}`}>
                {testEmailResult.message}
              </div>
            )}
            {testDigestResult && (
              <div className={`test-email-result ${testDigestResult.success ? 'success' : 'error'}`}>
                {testDigestResult.message}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de configuration des emails */}
      {showConfigModal && ReactDOM.createPortal(
        <div
          onClick={() => setShowConfigModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '40px 20px',
            overflowY: 'auto'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              maxWidth: '700px',
              width: '100%',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden',
              margin: 'auto 0'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              flexShrink: 0
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Paramètres des alertes email</h2>
              <button onClick={() => setShowConfigModal(false)} className="btn-icon">
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {/* Email consolidé */}
              <div className="notification-card" style={{ margin: 0 }}>
                <div className="notification-card-body">
                  <div className="notification-option">
                    <div className="notification-option-info">
                      <div className="option-title-row">
                        <Users size={20} />
                        <h3>Email consolidé</h3>
                      </div>
                      <p>
                        Un seul email récapitulatif avec tous vos comptes (ou une sélection).
                      </p>
                      {prefs.consolidatedEmailEnabled && (
                        <div className="selected-count">
                          {consolidatedAccountsCount === 0
                            ? `Tous les comptes (${accounts.length})`
                            : `${consolidatedAccountsCount} compte(s) sélectionné(s)`}
                        </div>
                      )}
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={prefs.consolidatedEmailEnabled}
                        onChange={(e) => handleConsolidatedToggle(e.target.checked)}
                        disabled={saving}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  {prefs.consolidatedEmailEnabled && (
                    <div className="accounts-selector">
                      <button
                        className="accounts-toggle-btn"
                        onClick={() => setShowConsolidatedAccounts(!showConsolidatedAccounts)}
                      >
                        <span>Sélectionner les comptes</span>
                        {showConsolidatedAccounts ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>

                      {showConsolidatedAccounts && (
                        <div className="accounts-list">
                          <div className="accounts-actions">
                            <button onClick={handleSelectAllConsolidated} className="link-btn">
                              Tous les comptes
                            </button>
                          </div>
                          {accounts.map(account => (
                            <label key={account.accountId} className="account-checkbox">
                              <input
                                type="checkbox"
                                checked={consolidatedAccountsCount === 0 || isAccountInList(account.accountId, prefs.consolidatedEmailAccounts)}
                                onChange={() => handleConsolidatedAccountToggle(account.accountId)}
                                disabled={saving}
                              />
                              <span className="account-name">{account.accountName || account.accountId}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Emails individuels */}
              <div className="notification-card" style={{ margin: 0 }}>
                <div className="notification-card-body">
                  <div className="notification-option">
                    <div className="notification-option-info">
                      <div className="option-title-row">
                        <User size={20} />
                        <h3>Emails individuels</h3>
                      </div>
                      <p>
                        Un email séparé pour chaque compte sélectionné (en plus de l'email consolidé).
                      </p>
                      {individualAccountsCount > 0 && (
                        <div className="selected-count">
                          {individualAccountsCount} compte(s) sélectionné(s)
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="accounts-selector">
                    <button
                      className="accounts-toggle-btn"
                      onClick={() => setShowIndividualAccounts(!showIndividualAccounts)}
                    >
                      <span>Sélectionner les comptes</span>
                      {showIndividualAccounts ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>

                    {showIndividualAccounts && (
                      <div className="accounts-list">
                        <div className="accounts-actions">
                          <button onClick={handleSelectAllIndividual} className="link-btn">
                            Tout sélectionner
                          </button>
                          <button onClick={handleSelectNoneIndividual} className="link-btn">
                            Tout désélectionner
                          </button>
                        </div>
                        {accounts.map(account => (
                          <label key={account.accountId} className="account-checkbox">
                            <input
                              type="checkbox"
                              checked={isAccountInList(account.accountId, prefs.individualEmailAccounts)}
                              onChange={() => handleIndividualAccountToggle(account.accountId)}
                              disabled={saving}
                            />
                            <span className="account-name">{account.accountName || account.accountId}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Info box */}
              <div className="notification-info-box" style={{ margin: 0 }}>
                <h4>Comment ça fonctionne ?</h4>
                <ul>
                  <li>Chaque nuit à 3h, vos comptes Google Ads sont automatiquement analysés</li>
                  <li>À 6h, vous recevez vos emails selon vos préférences</li>
                  <li>L'email consolidé résume tous vos comptes en un seul message</li>
                  <li>Les emails individuels donnent le détail pour chaque compte sélectionné</li>
                </ul>
              </div>
            </div>

            <div className="modal-footer" style={{ flexShrink: 0 }}>
              <button
                onClick={() => setShowConfigModal(false)}
                className="btn-primary"
                style={{ background: '#1a1a1a', border: 'none' }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de configuration du digest */}
      {showDigestConfigModal && ReactDOM.createPortal(
        <div
          onClick={() => setShowDigestConfigModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '40px 20px',
            overflowY: 'auto'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              maxWidth: '700px',
              width: '100%',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden',
              margin: 'auto 0'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              flexShrink: 0
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Paramètres du digest quotidien</h2>
              <button onClick={() => setShowDigestConfigModal(false)} className="btn-icon">
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {/* Sélection des comptes */}
              <div className="notification-card" style={{ margin: 0 }}>
                <div className="notification-card-body">
                  <div className="notification-option">
                    <div className="notification-option-info">
                      <div className="option-title-row">
                        <BarChart2 size={20} />
                        <h3>Comptes à inclure</h3>
                      </div>
                      <p>
                        Sélectionnez les comptes publicitaires dont vous souhaitez recevoir le résumé quotidien.
                      </p>
                      <div className="selected-count">
                        {digestAccountsCount === 0
                          ? `Tous les comptes (${digestAccounts.length})`
                          : `${digestAccountsCount} compte(s) sélectionné(s)`}
                      </div>
                    </div>
                  </div>

                  <div className="accounts-selector">
                    <button
                      className="accounts-toggle-btn"
                      onClick={() => setShowDigestAccounts(!showDigestAccounts)}
                    >
                      <span>Sélectionner les comptes</span>
                      {showDigestAccounts ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>

                    {showDigestAccounts && (
                      <div className="accounts-list">
                        <div className="accounts-actions">
                          <button onClick={handleSelectAllDigest} className="link-btn">
                            Tous les comptes
                          </button>
                        </div>
                        {digestAccounts.map(account => (
                          <label key={`${account.type}-${account.accountId}`} className="account-checkbox">
                            <input
                              type="checkbox"
                              checked={digestAccountsCount === 0 || isAccountInList(account.accountId, digestPrefs.digestEmailAccounts)}
                              onChange={() => handleDigestAccountToggle(account.accountId)}
                              disabled={savingDigest}
                            />
                            <span className="account-name" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {account.accountName || account.accountId}
                              {/* Une table plutôt qu'une suite de conditions : ajouter une
                                  régie au digest ne doit pas demander de retoucher le rendu,
                                  faute de quoi un compte apparaît sans logo. */}
                              {LOGOS_DIGEST[account.type] && (
                                <img
                                  src={LOGOS_DIGEST[account.type].src}
                                  alt={LOGOS_DIGEST[account.type].alt}
                                  style={{ width: '18px', height: '18px', objectFit: 'contain' }}
                                />
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Info box */}
              <div className="notification-info-box" style={{ margin: 0 }}>
                <h4>Contenu de l'email</h4>
                <ul>
                  <li>Chaque matin à 7h, vous recevez un récapitulatif de vos dépenses</li>
                  <li>Pour chaque compte : dépenses d'hier, des 30 derniers jours et du mois en cours</li>
                  <li>Un total général de toutes les dépenses</li>
                </ul>
              </div>
            </div>

            <div className="modal-footer" style={{ flexShrink: 0 }}>
              <button
                onClick={() => setShowDigestConfigModal(false)}
                className="btn-primary"
                style={{ background: '#1a1a1a', border: 'none' }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationsView;
